// src/components/album/MediaExpressionsModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Heart, 
  Smile, 
  ThumbsUp,
  Star,
  MessageCircle,
  Flame,
  Send,
  User,
  Clock
} from 'lucide-react';
import { albumService } from '../../services/albumService';
import { useAuth } from '../../contexts/AuthContext';

const EXPRESSION_TYPES = [
  { id: 'love', icon: Heart, label: 'Love', color: 'text-red-500' },
  { id: 'like', icon: ThumbsUp, label: 'Like', color: 'text-blue-500' },
  { id: 'wow', icon: Star, label: 'Amazing', color: 'text-yellow-500' },
  { id: 'fire', icon: Flame, label: 'Fire', color: 'text-orange-500' },
  { id: 'smile', icon: Smile, label: 'Happy', color: 'text-green-500' }
];

export const MediaExpressionsModal = ({ mediaItem, bookId, bookData, primaryColor, onClose }) => {
  const { user } = useAuth();
  const [expressions, setExpressions] = useState([]);
  const [userExpressions, setUserExpressions] = useState(new Set());
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);

  const [recordMode, setRecordMode] = useState('comment'); // 'comment' or 'team_record'
  const [recordCategory, setRecordCategory] = useState('');
  const [recordText, setRecordText] = useState('');

  const isTeamCategory = bookData?.albumSettings?.albumCategory === 'teams';
  const teamSubcategory = bookData?.albumSettings?.albumSubcategory;

  const getRecordCategories = () => {
    const categories = {
      corporate: [
        { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
        { id: 'performance', label: 'Performance', icon: '⭐', color: '#3b82f6' },
        { id: 'behavior', label: 'Behavior', icon: '😊', color: '#8b5cf6' },
        { id: 'achievement', label: 'Achievement', icon: '🏆', color: '#f59e0b' },
        { id: 'concern', label: 'Concern', icon: '⚠️', color: '#ef4444' },
        { id: 'goal', label: 'Goal', icon: '🎯', color: '#06b6d4' },
        { id: 'feedback', label: 'Feedback', icon: '💬', color: '#6366f1' },
        { id: 'training', label: 'Training', icon: '📚', color: '#14b8a6' }
      ],
      sports: [
        { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
        { id: 'performance', label: 'Performance', icon: '⭐', color: '#3b82f6' },
        { id: 'fitness', label: 'Fitness', icon: '💪', color: '#059669' },
        { id: 'injury', label: 'Injury', icon: '🤕', color: '#ef4444' },
        { id: 'achievement', label: 'Achievement', icon: '🏆', color: '#f59e0b' },
        { id: 'discipline', label: 'Discipline', icon: '⚖️', color: '#dc2626' },
        { id: 'skill', label: 'Skill Development', icon: '🎯', color: '#8b5cf6' }
      ],
      project: [
        { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
        { id: 'performance', label: 'Performance', icon: '⭐', color: '#3b82f6' },
        { id: 'taskProgress', label: 'Task Progress', icon: '✅', color: '#059669' },
        { id: 'collaboration', label: 'Collaboration', icon: '🤝', color: '#8b5cf6' },
        { id: 'issue', label: 'Issue', icon: '⚠️', color: '#ef4444' },
        { id: 'milestone', label: 'Milestone', icon: '🎯', color: '#f59e0b' },
        { id: 'feedback', label: 'Feedback', icon: '💬', color: '#6366f1' }
      ],
      volunteer: [
        { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
        { id: 'hours', label: 'Hours Logged', icon: '⏰', color: '#3b82f6' },
        { id: 'contribution', label: 'Contribution', icon: '✨', color: '#8b5cf6' },
        { id: 'achievement', label: 'Achievement', icon: '🏆', color: '#f59e0b' },
        { id: 'appreciation', label: 'Appreciation', icon: '❤️', color: '#ec4899' },
        { id: 'feedback', label: 'Feedback', icon: '💬', color: '#6366f1' }
      ]
    };

    return categories[teamSubcategory] || categories.corporate;
  };

  const recordCategories = getRecordCategories();

  useEffect(() => {
    loadExpressions();
  }, [mediaItem.id]);

  const loadExpressions = async () => {
    try {
      const data = await albumService.getMediaExpressions(bookId, mediaItem.galleryId, mediaItem.id, user.uid);
      setExpressions(data.expressions || []);
      
      if (user) {
        const userExpIds = new Set(
          (data.expressions || [])
            .filter(exp => exp.userId === user.uid && exp.action === 'expression')
            .map(exp => exp.type)
        );
        setUserExpressions(userExpIds);
      }
    } catch (error) {
      console.error('Error loading expressions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpressionToggle = async (expressionType) => {
    if (!user) {
      alert('Please sign in to add expressions');
      return;
    }

    try {
      const isCurrentlyExpressed = userExpressions.has(expressionType);
      
      await albumService.toggleMediaExpression(
        bookId,
        mediaItem.galleryId,
        mediaItem.id,
        expressionType,
        user.uid,
        {
          userName: user.displayName || 'Anonymous',
          userAvatar: user.photoURL
        }
      );

      // Update local state
      const newUserExpressions = new Set(userExpressions);
      if (isCurrentlyExpressed) {
        newUserExpressions.delete(expressionType);
      } else {
        newUserExpressions.add(expressionType);
      }
      setUserExpressions(newUserExpressions);
      
      // Reload expressions to get updated counts
      loadExpressions();
    } catch (error) {
      console.error('Error toggling expression:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (isTeamCategory && recordMode === 'team_record') {
      // Handle team record submission
      if (!recordCategory || !recordText.trim() || !user) return;
      
      try {
        // Import the service
        const { teamStatusService } = await import('../../services/statusService');
        
        const category = recordCategories.find(c => c.id === recordCategory);
        
        await teamStatusService.addStatusUpdate(
          bookId,
          mediaItem.galleryId,
          mediaItem.id,
          {
            statusType: 'team_record',
            statusId: recordCategory,
            statusLabel: category.label,
            statusIcon: category.icon,
            statusColor: category.color,
            notes: recordText.trim(),
            recordedBy: user.displayName || user.email || 'Manager',
            recordedByEmail: user.email,
            metadata: {
              recordCategory: recordCategory,
              mediaItemTitle: mediaItem.title,
              mediaItemName: mediaItem.metadata?.name,
              isDetailedRecord: true
            }
          }
        );

        setRecordText('');
        setRecordCategory('');
        loadExpressions(); // Reload to show new record
      } catch (error) {
        console.error('Error adding team record:', error);
      }
    } else {
      // Handle normal comment (existing logic)
      if (!newComment.trim() || !user) return;
      
      try {
        await albumService.addMediaComment(
          bookId,
          mediaItem.galleryId,
          mediaItem.id,
          {
            text: newComment.trim(),
            userId: user.uid,
            userName: user.displayName || 'Anonymous',
            userAvatar: user.photoURL,
            createdAt: new Date()
          }
        );

        setNewComment('');
        loadExpressions();
      } catch (error) {
        console.error('Error adding comment:', error);
      }
    }
  };

  const getExpressionCounts = () => {
    const counts = {};
    EXPRESSION_TYPES.forEach(type => {
      counts[type.id] = expressions.filter(exp => 
        exp.type === type.id && exp.action === 'expression'
      ).length;
    });
    return counts;
  };

  const getComments = () => {
    return expressions
      .filter(exp => exp.action === 'comment')
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Most recent first
      });
  };

  const formatDate = (timestamp) => {
    try {
      const date = timestamp?.toDate?.() || new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 30) return `${diffDays}d ago`;
      
      return date.toLocaleDateString();
    } catch (error) {
      return 'Unknown';
    }
  };

  const expressionCounts = getExpressionCounts();
  const comments = getComments();

  // PREVENT CLICK PROPAGATION
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Expressions & Comments</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Media Preview */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex gap-3">
            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              {mediaItem.type === 'image' ? (
                <img 
                  src={mediaItem.url} 
                  alt={mediaItem.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <video 
                  src={mediaItem.url} 
                  className="w-full h-full object-cover"
                  muted
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 truncate">
                {mediaItem.title || 'Untitled'}
              </h4>
              {mediaItem.description && (
                <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                  {mediaItem.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Expression Types */}
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Expressions</h4>
          <div className="flex flex-wrap gap-2">
            {EXPRESSION_TYPES.map((expr) => {
              const Icon = expr.icon;
              const count = expressionCounts[expr.id];
              const isActive = userExpressions.has(expr.id);
              
              return (
                <button
                  key={expr.id}
                  onClick={() => handleExpressionToggle(expr.id)}
                  disabled={!user}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
                    isActive
                      ? `font-medium text-white`
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
                  } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={isActive ? { backgroundColor: primaryColor } : {}}
                >
                  <Icon className="h-4 w-4" />
                  <span>{expr.label}</span>
                  {count > 0 && <span className="font-medium">({count})</span>}
                </button>
              );
            })}
          </div>
          {!user && (
            <p className="text-xs text-gray-500 mt-2">Sign in to add expressions</p>
          )}
        </div>

        {/* Comments Section */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">
              Comments ({comments.length})
            </h4>
          </div>

          {/* Comments List */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment, index) => (
                  <div key={comment.id || index} className="flex gap-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                      {comment.userAvatar ? (
                        <img 
                          src={comment.userAvatar} 
                          alt={comment.userName}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-4 w-4 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-gray-900">
                            {comment.userName}
                          </span>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="h-3 w-3" />
                            {formatDate(comment.createdAt)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No comments yet</p>
                <p className="text-xs">Be the first to comment!</p>
              </div>
            )}
          </div>

          {/* Comment/Record Input */}
          {user ? (
            <div className="px-4 py-3 border-t border-gray-200">
              {/* Mode Toggle for Team Category */}
              {isTeamCategory && (
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setRecordMode('comment')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      recordMode === 'comment'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    💬 Comment
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecordMode('team_record')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      recordMode === 'team_record'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    📝 Team Record
                  </button>
                </div>
              )}

              <form onSubmit={handleAddComment} className="space-y-3">
                {/* Category Selector for Team Records */}
                {isTeamCategory && recordMode === 'team_record' && (
                  <select
                    value={recordCategory}
                    onChange={(e) => setRecordCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    style={{ focusRingColor: primaryColor }}
                    required
                  >
                    <option value="">Select category...</option>
                    {recordCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                )}

                {/* Input Field */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={recordMode === 'team_record' ? recordText : newComment}
                    onChange={(e) => {
                      if (recordMode === 'team_record') {
                        setRecordText(e.target.value);
                      } else {
                        setNewComment(e.target.value);
                      }
                    }}
                    placeholder={
                      recordMode === 'team_record'
                        ? 'Add record details...'
                        : 'Add a comment...'
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    style={{ focusRingColor: primaryColor }}
                    maxLength={500}
                  />
                  <button
                    type="submit"
                    disabled={
                      recordMode === 'team_record'
                        ? !recordCategory || !recordText.trim()
                        : !newComment.trim()
                    }
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Sign in to add {isTeamCategory ? 'comments and records' : 'comments'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};